import { Button } from '@parrot-software/design-system';
import { ServerCrash, ShieldAlert, WifiOff } from '@parrot-software/design-system/icons';
import type { LucideIcon } from '@parrot-software/design-system/icons';
import { useTranslation } from 'react-i18next';

import { isOfflineFailure, MAGIC_LINK_ERROR_CODES } from '@identity/application/errors';
import { IdentityWizardLayout } from '@identity/presentation/components/base/IdentityWizardLayout';
import { StepCard } from '@identity/presentation/components/base/StepCard';

/**
 * The five error screens of a flow.
 *
 * `NETWORK` and `SERVER` are not `MagicLinkErrorCode`s on purpose: neither says anything about the
 * link, so they are the two cases that offer a retry instead of sending the user to sign in. They
 * are kept apart because only one of them is the user's to fix.
 */
export const LINK_ERROR_SCREEN_CODES = [...MAGIC_LINK_ERROR_CODES, 'NETWORK', 'SERVER'] as const;

export type LinkErrorScreenCode = typeof LINK_ERROR_SCREEN_CODES[number];

/** The codes that are about a failed call and not about the link, so running it again may work. */
const RETRYABLE_CODES: readonly LinkErrorScreenCode[] = ['NETWORK', 'SERVER'];

/** The icon of each code: the connection, our own side, or the link itself. */
const CODE_ICONS: Record<LinkErrorScreenCode, LucideIcon> = {
  LINK_EXPIRED: ShieldAlert,
  LINK_COMPLETED: ShieldAlert,
  LINK_INVALID: ShieldAlert,
  NETWORK: WifiOff,
  SERVER: ServerCrash,
};

/** Whether a code offers a retry of its own. */
export const isRetryableLinkErrorCode = (code: LinkErrorScreenCode): boolean => RETRYABLE_CODES.includes(code);

/**
 * The screen for a failure that is not about the link: the connection when no answer arrived, our
 * own side for anything else.
 *
 * Telling someone to check their internet because our API answered a 500 sends them to fix
 * something that was never broken, and hides that the fault was ours.
 */
export const nonLinkErrorCode = (error: unknown): LinkErrorScreenCode =>
  isOfflineFailure(error) ? 'NETWORK' : 'SERVER';

/** The texts of a flow that has no link, already translated. */
export type LinkErrorScreenCopy = {
  title: string;
  body: string;
  /** Label of the retry button. Absent in a flow that has nothing to retry. */
  retryLabel?: string;
};

export type LinkErrorScreenProps = {
  code: LinkErrorScreenCode;
  /**
   * Replaces the texts of `code`. Two callers need it: a flow where there is no link, or where the
   * link is fine and what failed is the front end finding a requirement it cannot paint — telling
   * those users to check their link would send them to repeat something they already did right —
   * and a flow that knows what its link was for, since the default texts must serve every purpose
   * and therefore name none.
   *
   * It arrives translated, never as an i18n key.
   */
  copy?: LinkErrorScreenCopy;
  /**
   * Runs the action again. With `copy` it is always offered; without it, only the two codes that are
   * about a failed call show it.
   */
  onRetry?: () => void;
  /** Absent in the flows that already have a session: there is nowhere to sign in to. */
  onSignIn?: () => void;
};

/**
 * One component, five contents: link expired, link already used, link invalid, no connection, our
 * own side — plus whatever copy a flow without links hands it.
 *
 * It keeps the frame of the flow it interrupts, so the screen the user was on does not change shape
 * under them. The frame carries a photograph and no progress list, which is what makes it usable
 * here: nothing counts steps that will never happen.
 *
 * Navigation arrives as callbacks instead of a router hook, so the screen can be mounted on its own
 * and each caller decides what retry means.
 *
 * @param {LinkErrorScreenProps} props - Component properties
 */
export const LinkErrorScreen = ({ code, copy, onRetry, onSignIn }: LinkErrorScreenProps) => {
  const { t } = useTranslation('identity');
  const canRetry = Boolean(onRetry) && (Boolean(copy) || isRetryableLinkErrorCode(code));
  const Icon = CODE_ICONS[code];

  return (
    <IdentityWizardLayout>
      <div className="w-full" data-error-code={code} data-testid="link-error-screen">
        <StepCard
          description={copy?.body ?? t(`linkError.${code}.body`)}
          icon={
            <span className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
              <Icon aria-hidden className="size-4 text-destructive" />
            </span>
          }
          title={copy?.title ?? t(`linkError.${code}.title`)}
        >
          <div className="flex flex-col items-stretch gap-3 sm:items-start">
            {canRetry && (
              <Button className="w-full sm:w-fit" onClick={onRetry}>
                {copy?.retryLabel ?? t('linkError.retryAction')}
              </Button>
            )}
            {onSignIn && (
              <Button className="w-full sm:w-fit" onClick={onSignIn} variant={canRetry ? 'outline' : 'default'}>
                {isRetryableLinkErrorCode(code) ? t('linkError.retrySecondaryAction') : t(`linkError.${code}.action`)}
              </Button>
            )}
          </div>
        </StepCard>
      </div>
    </IdentityWizardLayout>
  );
};
